import { Injectable } from '@angular/core';
import { AttributeInterface } from 'src/app/model/attributes/attribute-interface';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import { NgModel, AbstractControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class UtilService {
    constructor() { }
    private readonly dirty$ = new BehaviorSubject<boolean>(false);

    isDirty$(): Observable<boolean> {
        return this.dirty$.asObservable();
    }

    isDirty(): boolean {
        return this.dirty$.getValue();
    }

    setDirty(dirty: boolean): void {
        this.dirty$.next(dirty);
    }

    isStringNullOrEmpty(val: string): boolean {
        if (val === undefined || val === null || val.trim() === '') {
            return true;
        }
        return false;
    }

    transformAttributeValue(attributeValue): string {
        if (attributeValue != null) {
            return attributeValue.replace(/\;/g, ', ');
        }
    }

    stripHTML(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    }

    sortByName(objectArray: any[]) {
        objectArray.sort((a, b) => {
            var nameA = a.name.toUpperCase();
            var nameB = b.name.toUpperCase();

            return nameA.localeCompare(nameB);
        });
    }

    public checkAttrDatatype(
        attribute: AttributeInterface,
        validationValue: string
    ) {
        let result: any;
        switch (attribute.datatype) {
            case 'NUMERIC': {
                result = parseInt(validationValue);
                break;
            }
            case 'BOOLEAN': {
                result =
                    validationValue == null || validationValue === 'undefined'
                        ? null
                        : validationValue === 'true';
                break;
            }
            case 'LIST': {
                if (validationValue) {
                    result = validationValue.split(';');
                }
                break;
            }
        }
        return result;
    }

    /**
     * helper method to check for string lengths of the name and description of entities ( name 255, description 10000 letters
     */
    public static areStringsValid(name: string, description: string) {
        let checkName: boolean = true;
        if (name) {
            checkName = name.length < 161;
        }

        let checkDescr: boolean = true;
        if (description) {
            checkDescr = description.length < 10001;
        }

        return checkName && checkDescr;
    }

    public getTransformedAttributeValue(dataType: string, value: string) {
        if (dataType === 'BOOLEAN') {
            if (value == null) {
                return 'Keine Angabe';
            } else if (value) {
                return 'Ja';
            } else {
                return 'Nein';
            }
        } else if (dataType === 'LIST') {
            if (value != null) {
                return value.replace(/;/g, ', ');
            }
        }
        return value;
    }

    /**
     * Checks if user input is a valid international number
     * @param phone - input value
     * @param defaultCountry - By default its set to 'DE'
     * @returns 
     */
    public validateInternationalPhone(phone: string, defaultCountry: CountryCode = 'DE'): boolean {
        if (!phone) return false;
        const cleaned = phone.trim().replace(/\s+/g, '');
        const parsed = parsePhoneNumberFromString(cleaned, cleaned.startsWith('+') ? undefined : defaultCountry);
        return parsed ? parsed.isValid() : false;
    }

    /**
     * Detects phone type based on number type (mobile or fixed line).
     * @param phone - input value
     * @param defaultCountry  - By default its set to 'DE'
     * @returns 
     */
    public detectPhoneType(phone: string, defaultCountry: CountryCode = 'DE'): 'mobile' | 'landline' | 'unknown' {
        if (!phone) return 'unknown';
        const cleaned = phone.trim();
        const parsed = parsePhoneNumberFromString(cleaned, cleaned.startsWith('+') ? undefined : defaultCountry);
        if (!parsed) return 'unknown';

        const type = parsed.getType();
        if (type === 'MOBILE') return 'mobile';
        if (type === 'FIXED_LINE') return 'landline';
        return 'unknown';
    }


    /**
     * Checks whether a description string contains at least 4 (showInitialLinesAmount) <p> tags, or if it is too long.
     * This matters since each <p> inside the enclosing tag causes a new line, thus 4 <p> tags result in 4 lines of text,
     * which is the amount initially displayed
     */
    public containsTooManyParagraphs(description: string, showInitialLinesAmount: number): boolean {
        let tooManyParagraphsOrTooLong: boolean = false;
        const pCount: number = (description.match(/\n/g) || []).length;
        if (pCount >= showInitialLinesAmount) {
            return (tooManyParagraphsOrTooLong = true);
        }
        if (description.length > 200) {
            return (tooManyParagraphsOrTooLong = true);
        }
        return tooManyParagraphsOrTooLong;
    }

    /**
     * Cap too long reviewTexts/ reviewTexts with too many paragraphs to a shortened text and add "..." to the end
     * This shortened text is then displayed by default, the full reviewText is visible when the user clicks "Mehr anzeigen"
     * @param reviewText the original (full length) review text that is too long/has to many paragraphs to be fully displayed
     * @returns shortReviewText to be displayed in product detail page
     */
    public generateShortReviewText(reviewText: string, showInitialLinesAmount: number): string {
        let shortReviewText: string = reviewText.substr(0, 199);
        let splitted: string[] = shortReviewText.split(/\n/g);
        if (splitted.length >= showInitialLinesAmount) {
            shortReviewText = '';
            for (let i = 0; i < showInitialLinesAmount; i++) {
                shortReviewText += splitted[i] + '\n';
            }
        }
        shortReviewText += '...';
        return shortReviewText;
    }


    /**
     * Checks if the input value exceeds the maximum length and sets an error on the control if it does.
     * If the value is within the limit, it removes the error.
     * @param control NgModel control to set or remove errors from
     * @param value input value to check
     * @param maxLength defines the maximum allowed length
     */
    static checkMaxLength(control: NgModel, value: string | undefined, maxLength: number): void {
        if (value && value.length > maxLength) {
            this.setError(control, 'maxlength', true);
        } else {
            this.removeError(control, 'maxlength');
        }
    }

    private static setError(control: NgModel, errorKey: string, errorValue: any): void {
        const currentErrors = control.control.errors || {};
        currentErrors[errorKey] = errorValue;
        control.control.setErrors(currentErrors);
    }

    private static removeError(control: NgModel, errorKey: string): void {
        if (!control.control.errors?.[errorKey]) return;

        const errors = { ...control.control.errors };
        delete errors[errorKey];
        control.control.setErrors(Object.keys(errors).length ? errors : null);
    }

    /**
     * returns an error message for a given control and field name.
     * @param control NgModel control to check
     * @param field - the field name to be used in error messages
     * @returns 
     */
    getErrorMessage(
        control: NgModel,
        options: {
            maxLength?: number;
            includeMinLength?: boolean;
            fieldName?: string;
            fieldDescription?: string;
            customMessages?: {
                required?: string;
                maxlength?: string;
                minlength?: string;
            }
        } = {}
    ): string {
        const {
            maxLength = 160,
            includeMinLength = false,
            fieldName = '',
            fieldDescription = '',
            customMessages = {}
        } = options;

        // Required Error
        if (control.errors?.required) {
            return customMessages.required ||
                (fieldName ? `Bitte geben Sie einen ${fieldName}namen ein` : fieldDescription ? `Bitte geben Sie eine ${fieldDescription} ein` : "Diese Angabe ist erforderlich");
        }

        // MaxLength Error 
        if (control.errors?.maxlength || (control.value?.length || 0) > maxLength) {
            const actualMaxLength = control.errors?.maxlength?.requiredLength || maxLength;
            return customMessages.maxlength ||
                (fieldName ?
                    `Der ${fieldName}name darf maximal ${actualMaxLength} Zeichen lang sein` :
                    `Die Angabe darf maximal ${actualMaxLength} Zeichen lang sein`);
        }

        // MinLength Error (if it´s activated)
        if (includeMinLength && (control.touched || control.dirty)) {
            if (control.errors?.minlength) {
                const requiredMinLength = control.errors.minlength.requiredLength;
                return customMessages.minlength ||
                    (fieldName ?
                        `Der ${fieldName}name muss mindestens ${requiredMinLength} Zeichen lang sein` :
                        `Die Angabe muss mindestens ${requiredMinLength} Zeichen lang sein`);
            }
            // Fallback
            if ((control.value?.length || 0) < 2) {
                return customMessages.minlength ||
                    (fieldName ?
                        `Der ${fieldName}name muss mindestens 2 Zeichen lang sein` :
                        `Die Angabe muss mindestens 2 Zeichen lang sein`);
            }
        }

        return '';
    }

    getFormControlErrorMessage(
        control: AbstractControl,
        options: {
            maxLength?: number;
            includeMinLength?: boolean;
            fieldName?: string;
            customMessages?: {
                required?: string;
                maxlength?: string;
                minlength?: string;
            }
        } = {}
    ): string {
        const {
            maxLength = 160,
            includeMinLength = false,
            fieldName = '',
            customMessages = {}
        } = options;

        // Required Error
        if (control.hasError('required')) {
            return customMessages.required ||
                (fieldName ? `Bitte geben Sie einen ${fieldName}name ein` : "Diese Angabe ist erforderlich");
        }

        // MaxLength Error
        if (control.hasError('maxlength')) {
            const actualMaxLength = control.getError('maxlength').requiredLength;
            return customMessages.maxlength ||
                (fieldName ?
                    `Der ${fieldName}name darf maximal ${actualMaxLength} Zeichen lang sein` :
                    `Die Angabe darf maximal ${actualMaxLength} Zeichen lang sein`);
        }

        // MinLength Error (if it´s activated)
        if (includeMinLength && control.hasError('minlength') && (control.touched || control.dirty)) {
            const requiredMinLength = control.getError('minlength').requiredLength;
            return customMessages.minlength ||
                (fieldName ?
                    `Der ${fieldName}name muss mindestens ${requiredMinLength} Zeichen lang sein` :
                    `Die Angabe muss mindestens ${requiredMinLength} Zeichen lang sein`);
        }

        return '';
    }


    decodeValue(val: string): any {
        if (!val) return val;

        const isJwt = (s: any) => typeof s === 'string' && s.startsWith('eyJ') && s.split('.').length === 3;
        const decode = (token: string): any => {
            try {
                const parts = token.split('.');
                // Base64Url to Base64
                let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                // Pad if necessary
                while (base64.length % 4 !== 0) {
                    base64 += '=';
                }
                const payload = JSON.parse(atob(base64));

                // Gaia-X/W3C VC: Check for nested JWTs in verifiableCredential array
                // The user says "the vcJWT consist of 3 VCJWT", which usually means they are inside the VP
                if (payload.vp && Array.isArray(payload.vp.verifiableCredential)) {
                    payload.vp.verifiableCredential = payload.vp.verifiableCredential.map(vc => isJwt(vc) ? decode(vc) : vc);
                } else if (Array.isArray(payload.verifiableCredential)) {
                    payload.verifiableCredential = payload.verifiableCredential.map(vc => isJwt(vc) ? decode(vc) : vc);
                }

                return payload;
            } catch {
                return token;
            }
        };

        // Try to parse as JSON first
        try {
            const parsed = JSON.parse(val);

            // If it's an array of strings, check if they are JWTs
            if (Array.isArray(parsed)) {
                return parsed.map(item => isJwt(item) ? decode(item) : item);
            }

            // Check if the object itself has a property containing a JWT (e.g., { "vcJwt": "..." })
            if (parsed && typeof parsed === 'object') {
                const jwtKeys = ['vcJwt', 'vc-jwt', 'vc_jwt', 'jwt'];
                jwtKeys.forEach(key => {
                    if (isJwt(parsed[key])) {
                        parsed[key] = decode(parsed[key]);
                    }
                });
            }

            if (isJwt(parsed)) return decode(parsed);
            return parsed;
        } catch {
            // If it's not JSON, check if it's a single JWT or multiple space-separated JWTs
            if (isJwt(val)) return decode(val);

            // Handle space-separated tokens
            const tokens = val.trim().split(/\s+/);
            if (tokens.length > 1 && tokens.every(isJwt)) {
                return tokens.map(decode);
            }

            return val;
        }
    }

}
